// AssignPatients.js — Verifier auto-assignment (block queue).
//
// Replaces the old Quick Assigner Notes / Build / bulk-assign workflow.
// Verifiers now pull work themselves; the person at /assign-patients only
// watches who's doing what and can Mark priority / Remove a file.
//
// Same route serves two different views depending on who's logged in:
//   - Admin / Admin Verifier (type 1 / 8) -> AssignerMonitor
//   - Verifier (type 10)                 -> VerifierQueuePanel
import React, { Component } from "react";
import axios from "axios";
import { apiUrl } from "../../config/config";
import SideBar from "../common/SideBar";
import { performLogout } from "../../utils/logout";
import "./AssignPatients.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// 02-Nov-2026 14:05 — day-Mon-year, 24h time. Used everywhere on this page
// instead of the locale-default month-first format, which can be misread.
function fmtDate(raw) {
  if (!raw) return "";
  const dt = new Date(raw);
  if (isNaN(dt.getTime())) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return dd + "-" + MONTHS[dt.getMonth()] + "-" + dt.getFullYear() + " " + hh + ":" + mi;
}
function fmtTime(raw) {
  if (!raw) return "";
  const dt = new Date(raw);
  if (isNaN(dt.getTime())) return "";
  return String(dt.getHours()).padStart(2, "0") + ":" + String(dt.getMinutes()).padStart(2, "0");
}

const SIDEBAR_MENUS = [
  { title: "Patient List", url: "/patient-list" },
  { title: "Verifier Home", url: "/verifier-home" },
  { title: "Analysis Dashboard", url: "/analysis-dashboard" },
  { title: "Users by Role", url: "/users-by-role" },
  { title: "Telemetry", url: "/telemetry" },
  { title: "Assign Patients", url: "/assign-patients" },
  { title: "Verified", url: "/Verification-Completed" },
  { title: "QC", url: "/qc_home" },
];

function Shell({ children, onLogout }) {
  return (
    <div className="ap-root">
      <SideBar menus={SIDEBAR_MENUS} profileLink="verifier-profile" hideProfile={true} hideLogout={true} />
      <div className="ap-page">{children}</div>
    </div>
  );
}

const BLOCK_LABEL = {
  finished: (l) => l + " done",
  assigned: (l) => l + " doing",
  reanalysis: (l) => l + " reanalysis",
};

function BlockPill({ block }) {
  let cls = "ap-blockpill ap-blockpill--waiting";
  let label = block.blockLabel;
  if (block.status === "finished") { cls = "ap-blockpill ap-blockpill--done"; label = BLOCK_LABEL.finished(label); }
  else if (block.status === "assigned") { cls = "ap-blockpill ap-blockpill--doing"; label = BLOCK_LABEL.assigned(label); }
  else if (block.status === "reanalysis") { cls = "ap-blockpill ap-blockpill--reanalysis"; label = BLOCK_LABEL.reanalysis(label); }
  else if (block.isNext) { cls = "ap-blockpill ap-blockpill--next"; label = label + " next"; }
  return <span className={cls}>{label}</span>;
}

// ── Assigner monitor ──────────────────────────────────────────────────────
const MONITOR_POLL_MS = 5 * 60 * 1000;

class AssignerMonitor extends Component {
  state = {
    data: null,
    loading: true,
    error: "",
    search: "",
    serverFilter: "all",
    busyKey: "",
  };

  componentDidMount() {
    this.load();
    this._poll = setInterval(this.load, MONITOR_POLL_MS);
  }
  componentWillUnmount() {
    if (this._poll) clearInterval(this._poll);
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.get(apiUrl + "api/users/verifierQueue/monitor?_t=" + Date.now());
      this.setState({ data: res.data, loading: false, error: "" });
    } catch (err) {
      this.setState({
        loading: false,
        error: (err.response && err.response.data && err.response.data.error) || err.message || "Failed to load",
      });
    }
  };

  act = async (key, path, body) => {
    this.setState({ busyKey: key });
    try {
      await axios.post(apiUrl + "api/users/verifierQueue/" + path, body);
      await this.load();
    } catch (err) {
      this.setState({
        error: (err.response && err.response.data && err.response.data.error) || err.message || "Action failed",
      });
    } finally {
      this.setState({ busyKey: "" });
    }
  };

  markPriority = (row) => this.act("p:" + row.fileId, "setPriority", { server: row.server, fileId: row.fileId, isPriority: !row.isPriority });
  remove = (row) => this.act("r:" + row.fileId, "remove", { server: row.server, fileId: row.fileId });
  putBack = (row) => this.act("b:" + row.fileId, "putback", { server: row.server, fileId: row.fileId });

  matchesSearch(row, q) {
    if (!q) return true;
    const hay = (String(row.deviceId || "") + " " + String(row.name || "")).toLowerCase();
    return hay.indexOf(q) !== -1;
  }

  render() {
    const { data, loading, error, search, serverFilter } = this.state;
    const q = search.trim().toLowerCase();

    const servers = data
      ? Array.from(new Set(data.queue.concat(data.held || []).map((r) => r.server))).sort()
      : [];
    const passServer = (r) => serverFilter === "all" || r.server === serverFilter;

    const queue = data ? data.queue.filter((r) => passServer(r) && this.matchesSearch(r, q)) : [];
    const held = data ? (data.held || []).filter((r) => passServer(r) && this.matchesSearch(r, q)) : [];
    const removed = data ? data.removed.filter((r) => passServer(r) && this.matchesSearch(r, q)) : [];
    const finished = data ? data.finished.filter((r) => passServer(r) && this.matchesSearch(r, q)) : [];
    const verifiers = data ? data.verifiers : [];

    const doingCount = verifiers.filter((v) => v.status === "doing").length;
    const freeCount = verifiers.filter((v) => v.status === "free").length;
    const pendingCount = queue.filter((r) => /^Pending/.test(r.status)).length;

    return (
      <Shell onLogout={this.props.onLogout}>
        <div className="ap-header">
          <div>
            <h2>Assignment monitor</h2>
            <span className="ap-sub">
              {verifiers.length} verifier{verifiers.length === 1 ? "" : "s"} &middot; <b>{doingCount}</b> doing &middot; <b>{freeCount}</b> free &middot; <b>{queue.length}</b> in queue &middot; <b>{pendingCount}</b> pending
              {data && (
                <>
                  {" · "}
                  <span style={{ opacity: 0.7 }}>updated {fmtDate(data.updatedAt)} · refreshes every 5 min</span>
                </>
              )}
            </span>
          </div>
          <div className="ap-header__actions">
            <button className="ap-btn ap-btn--ghost" onClick={this.load} disabled={loading}>
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button className="ap-btn ap-btn--logout" onClick={this.props.onLogout} title="Sign out">🚪 Logout</button>
          </div>
        </div>

        {error && <div className="ap-error">{error}</div>}

        <div className="ap-filters">
          <input
            className="ap-search"
            placeholder="Search patient ID or name…"
            value={search}
            onChange={(e) => this.setState({ search: e.target.value })}
          />
          <select className="ap-search ap-search--narrow" value={serverFilter} onChange={(e) => this.setState({ serverFilter: e.target.value })}>
            <option value="all">All servers</option>
            {servers.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <h3 className="ap-sectionTitle">Verifiers right now</h3>
        <div className="ap-vgrid">
          {verifiers.length === 0 && <div className="ap-empty">No verifiers found.</div>}
          {verifiers.map((v) => (
            <div key={v.email || v.name} className={"ap-vcard" + (v.status === "doing" ? " ap-vcard--doing" : "")}>
              <div className="ap-vcard__head">
                <span className="ap-vcard__name">{v.name}</span>
                <span className={"ap-vbadge " + (v.status === "doing" ? "ap-vbadge--doing" : "ap-vbadge--free")}>
                  {v.status === "doing" ? "Doing" : "Free"}
                </span>
              </div>
              {v.current && (
                <div className="ap-vcard__current">
                  <span className="ap-mono">{v.current.deviceId} {v.current.blockLabel} {String(v.current.server).toUpperCase()}</span>
                  <span className="ap-vcard__patient">{v.current.name}</span>
                  <span className="ap-vcard__since">since {fmtTime(v.current.assignedAt)}</span>
                </div>
              )}
              {v.reanalysisWaiting.map((r) => (
                <div className="ap-vcard__reanalysis" key={r.fileId + ":" + r.blockLabel}>
                  Waiting for reanalysis: {r.deviceId} {r.blockLabel} {String(r.server).toUpperCase()}
                </div>
              ))}
            </div>
          ))}
        </div>

        <h3 className="ap-sectionTitle">Queue</h3>
        <p className="ap-sectionHint">
          Priority first, then oldest first. 7-day files go last unless marked priority. Each new request gets the block marked next, then the block after it, so one ID can be with several verifiers at once.
        </p>
        <div className="ap-table-wrap">
          <table className="ap-table ap-table--queue">
            <colgroup>
              <col style={{ width: "5%" }} /><col style={{ width: "11%" }} /><col style={{ width: "7%" }} />
              <col style={{ width: "8%" }} /><col style={{ width: "14%" }} /><col style={{ width: "7%" }} />
              <col style={{ width: "22%" }} /><col style={{ width: "13%" }} /><col style={{ width: "13%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Order</th><th>Uploaded</th><th>Server</th><th>Device ID</th><th>Patient</th>
                <th>Length</th><th>Block order</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 && held.length === 0 && (
                <tr><td colSpan={9} className="ap-empty">Nothing in the queue.</td></tr>
              )}
              {queue.map((row) => {
                const key = row.server + ":" + row.fileId;
                const busy = this.state.busyKey === "p:" + row.fileId || this.state.busyKey === "r:" + row.fileId;
                return (
                  <tr key={key}>
                    <td>{row.order == null ? "—" : row.order}</td>
                    <td>{fmtDate(row.uploadTs)}</td>
                    <td className="ap-mono">{String(row.server).toUpperCase()}</td>
                    <td className="ap-mono">{row.deviceId}</td>
                    <td>
                      {row.name}
                      {row.isPriority && <span className="ap-badge ap-badge--priority">PRIORITY</span>}
                      {row.isPaced && <span className="ap-badge ap-badge--paced">PACED</span>}
                    </td>
                    <td>{row.length}</td>
                    <td className="ap-col-blocks">{row.blocks.map((b) => <BlockPill key={b.blockIndex} block={b} />)}</td>
                    <td className={/^Pending|^Reanalysis/.test(row.status) ? "ap-status--warn" : ""}>{row.status}</td>
                    <td className="ap-col-actions">
                      <button className="ap-btn ap-btn--ghost ap-btn--sm" disabled={busy} onClick={() => this.markPriority(row)}>
                        {row.isPriority ? "Remove priority" : "Mark priority"}
                      </button>
                      <button className="ap-btn ap-btn--ghost ap-btn--sm ap-btn--danger" disabled={busy} onClick={() => this.remove(row)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
              {held.map((row) => {
                const key = "hold:" + row.server + ":" + row.fileId;
                const busy = this.state.busyKey === "p:" + row.fileId || this.state.busyKey === "r:" + row.fileId;
                return (
                  <tr key={key}>
                    <td>Hold</td>
                    <td>{fmtDate(row.uploadTs)}</td>
                    <td className="ap-mono">{String(row.server).toUpperCase()}</td>
                    <td className="ap-mono">{row.deviceId}</td>
                    <td>
                      {row.name}
                      {row.isPriority && <span className="ap-badge ap-badge--priority">PRIORITY</span>}
                      {row.isPaced && <span className="ap-badge ap-badge--paced">PACED</span>}
                    </td>
                    <td>{row.length}</td>
                    <td className="ap-col-blocks">
                      {(row.blockLabels || []).map((label, i) => (
                        <span className="ap-blockpill ap-blockpill--waiting" key={i}>{label}</span>
                      ))}
                    </td>
                    <td className="ap-status--warn">{row.status}</td>
                    <td className="ap-col-actions">
                      <button className="ap-btn ap-btn--ghost ap-btn--sm" disabled={busy} onClick={() => this.markPriority(row)}>
                        {row.isPriority ? "Remove priority" : "Mark priority"}
                      </button>
                      <button className="ap-btn ap-btn--ghost ap-btn--sm ap-btn--danger" disabled={busy} onClick={() => this.remove(row)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h3 className="ap-sectionTitle">Removed</h3>
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead><tr><th>Server</th><th>Device ID</th><th>Patient</th><th>Removed</th><th>By</th><th>Actions</th></tr></thead>
            <tbody>
              {removed.length === 0 && <tr><td colSpan={6} className="ap-empty">Nothing removed.</td></tr>}
              {removed.map((row) => (
                <tr key={row.server + ":" + row.fileId}>
                  <td className="ap-mono">{String(row.server).toUpperCase()}</td>
                  <td className="ap-mono">{row.deviceId}</td>
                  <td>{row.name}</td>
                  <td>{fmtDate(row.removedAt)}</td>
                  <td>{row.removedByName}</td>
                  <td>
                    <button className="ap-btn ap-btn--ghost ap-btn--sm" disabled={this.state.busyKey === "b:" + row.fileId} onClick={() => this.putBack(row)}>
                      Put back
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="ap-sectionTitle">Finished</h3>
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead><tr><th>Server</th><th>Device ID</th><th>Patient</th><th>Block</th><th>Finished</th><th>By</th></tr></thead>
            <tbody>
              {finished.length === 0 && <tr><td colSpan={6} className="ap-empty">Nothing finished yet.</td></tr>}
              {finished.map((row, i) => (
                <tr key={row.server + ":" + row.fileId + ":" + row.blockLabel + ":" + i}>
                  <td className="ap-mono">{String(row.server).toUpperCase()}</td>
                  <td className="ap-mono">{row.deviceId}</td>
                  <td>{row.name}</td>
                  <td>{row.blockLabel}</td>
                  <td>{fmtDate(row.finishedAt)}</td>
                  <td>{row.finishedByVerifierName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Shell>
    );
  }
}

// ── Verifier request page ─────────────────────────────────────────────────
const VERIFIER_POLL_MS = 30 * 1000;
const RETURN_MS = 2 * 60 * 60 * 1000;
const WARN_BEFORE_MS = 15 * 60 * 1000;

class VerifierQueuePanel extends Component {
  state = { mine: null, loading: true, error: "", msg: "", requesting: false, acting: false };

  componentDidMount() {
    this.load();
    this._poll = setInterval(this.load, VERIFIER_POLL_MS);
  }
  componentWillUnmount() {
    if (this._poll) clearInterval(this._poll);
  }

  load = async () => {
    try {
      const res = await axios.get(apiUrl + "api/users/verifierQueue/mine?_t=" + Date.now());
      this.setState({ mine: res.data, loading: false, error: "" });
    } catch (err) {
      this.setState({
        loading: false,
        error: (err.response && err.response.data && err.response.data.error) || err.message || "Failed to load",
      });
    }
  };

  request = async () => {
    this.setState({ requesting: true, msg: "", error: "" });
    try {
      const res = await axios.post(apiUrl + "api/users/verifierQueue/request", {});
      if (res.data && res.data.block) {
        this.setState({ msg: res.data.fromReanalysis ? "Your reanalysis block is back." : "New block assigned." });
      } else {
        this.setState({ msg: (res.data && res.data.message) || "Nothing available right now." });
      }
      await this.load();
    } catch (err) {
      this.setState({ error: (err.response && err.response.data && err.response.data.error) || err.message || "Request failed" });
    } finally {
      this.setState({ requesting: false });
    }
  };

  complete = async (block) => {
    this.setState({ acting: true, error: "" });
    try {
      await axios.post(apiUrl + "api/users/verifierQueue/complete", {
        server: block.server, fileId: block.fileId, blockIndex: block.blockIndex,
      });
      this.setState({ msg: "Marked completed — you're free." });
      await this.load();
    } catch (err) {
      this.setState({ error: (err.response && err.response.data && err.response.data.error) || err.message || "Failed" });
    } finally {
      this.setState({ acting: false });
    }
  };

  reanalysisNeeded = async (block) => {
    this.setState({ acting: true, error: "" });
    try {
      await axios.post(apiUrl + "api/users/verifierQueue/reanalysis", {
        server: block.server, fileId: block.fileId, blockIndex: block.blockIndex,
      });
      this.setState({ msg: "Parked for reanalysis — you can request a new block." });
      await this.load();
    } catch (err) {
      this.setState({ error: (err.response && err.response.data && err.response.data.error) || err.message || "Failed" });
    } finally {
      this.setState({ acting: false });
    }
  };

  render() {
    const { mine, loading, error, msg, requesting, acting } = this.state;
    const active = mine && mine.active;
    const reanalysis = (mine && mine.reanalysis) || [];

    let warn = false;
    let remainingMin = null;
    if (active && active.assignedAt) {
      const elapsed = Date.now() - new Date(active.assignedAt).getTime();
      remainingMin = Math.max(0, Math.round((RETURN_MS - elapsed) / 60000));
      warn = elapsed >= RETURN_MS - WARN_BEFORE_MS;
    }

    return (
      <Shell onLogout={this.props.onLogout}>
        <div className="ap-header">
          <div><h2>My verification queue</h2></div>
          <div className="ap-header__actions">
            <button className="ap-btn ap-btn--ghost" onClick={this.load} disabled={loading}>{loading ? "Refreshing…" : "↻ Refresh"}</button>
            <button className="ap-btn ap-btn--logout" onClick={this.props.onLogout} title="Sign out">🚪 Logout</button>
          </div>
        </div>

        {error && <div className="ap-error">{error}</div>}
        {msg && <div className="ap-flash">{msg}</div>}

        {active ? (
          <div className="ap-currentBlock">
            <div className="ap-currentBlock__title">Current block</div>
            <div className="ap-currentBlock__row">
              <span className="ap-mono">{active.deviceId} {active.blockLabel} {String(active.server).toUpperCase()}</span>
            </div>
            <div className="ap-currentBlock__row">Started {fmtDate(active.assignedAt)}</div>
            {warn && (
              <div className="ap-status--warn ap-currentBlock__warn">
                ⚠ Returns to the queue in {remainingMin} min if not marked completed.
              </div>
            )}
            <div className="ap-currentBlock__actions">
              <button className="ap-btn ap-btn--primary" disabled={acting} onClick={() => this.complete(active)}>Mark completed</button>
              <button className="ap-btn ap-btn--ghost" disabled={acting} onClick={() => this.reanalysisNeeded(active)}>Reanalysis needed</button>
            </div>
          </div>
        ) : (
          <div className="ap-currentBlock ap-currentBlock--free">
            <div className="ap-currentBlock__title">You're free</div>
            <button className="ap-btn ap-btn--primary" disabled={requesting} onClick={this.request}>
              {requesting ? "Requesting…" : "Request file"}
            </button>
          </div>
        )}

        {reanalysis.length > 0 && (
          <>
            <h3 className="ap-sectionTitle">Waiting on reanalysis</h3>
            <div className="ap-table-wrap">
              <table className="ap-table">
                <thead><tr><th>Server</th><th>Device ID</th><th>Block</th><th>Marked</th><th>Status</th></tr></thead>
                <tbody>
                  {reanalysis.map((r) => (
                    <tr key={r.server + ":" + r.fileId + ":" + r.blockIndex}>
                      <td className="ap-mono">{String(r.server).toUpperCase()}</td>
                      <td className="ap-mono">{r.deviceId}</td>
                      <td>{r.blockLabel}</td>
                      <td>{fmtDate(r.reanalysisMarkedAt)}</td>
                      <td>{r.ready ? "Ready — request to pick it back up" : "Waiting on reanalysis"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Shell>
    );
  }
}

export default class AssignPatients extends Component {
  state = { verifierType: undefined };

  componentDidMount() {
    this.setState({ verifierType: localStorage.getItem("verifiertype") });
  }

  handleLogout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await performLogout();
  };

  render() {
    const t = this.state.verifierType;
    if (t === undefined) return null;
    if (t === "1" || t === "8") return <AssignerMonitor onLogout={this.handleLogout} />;
    if (t === "10") return <VerifierQueuePanel onLogout={this.handleLogout} />;
    return (
      <Shell onLogout={this.handleLogout}>
        <div className="ap-error">This page is for Admin, Admin Verifier, or Verifier accounts only.</div>
      </Shell>
    );
  }
}
